/**
 * CSFixationFormDialog — Create / Edit Consignment-Sale Fixation (chốt giá NPE).
 *
 * Access reference: `frmPurchaseQryCSFixationAdd` (Caption "Purchase - CS
 * Fixation"). Shared style tokens from `purchase/dialogStyle.ts`.
 *
 * Fields per FE model:
 *   - contract_no (NPE / consignment), fixation_date, quantity_kg, price
 *   - p_contract_no (auto-bound LocalP, optional)
 *   - readonly displays: supplier_name (Vendor), quality_code, fixation_no
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { usePagePermission } from '@/hooks/usePagePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useListAllContractsQuery } from '@/store/api/contractsAllApi'
import {
  useCreateCSFixationMutation,
  useUpdateCSFixationMutation,
} from '@/store/api/csFixationsApi'
import { useListLocalPQuery } from '@/store/api/localPApi'
import type { CSFixationCreateIn, CSFixationOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
  sectionHeaderSx,
} from './dialogStyle'

export interface CSFixationFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  fixedNpeContractNo?: string
  initialValue?: CSFixationOut | null
  onClose: () => void
  onSaved?: (id: number) => void
}

interface CSFixFormValues {
  contract_no: string // NPE / Consignment
  fixation_date: string
  quantity_kg: number | ''
  price: number | ''
  p_contract_no: string
  note: string
}

const EMPTY: CSFixFormValues = {
  contract_no: '',
  fixation_date: dayjs().format('YYYY-MM-DD'),
  quantity_kg: '',
  price: '',
  p_contract_no: '',
  note: '',
}

const schema: yup.ObjectSchema<CSFixFormValues> = yup.object({
  contract_no: yup.string().trim().required('Consignment No is required'),
  fixation_date: yup.string().required('Date is required'),
  quantity_kg: yup
    .mixed<number | ''>()
    .test('required', 'Quantity is required', (v) => v !== '' && v !== undefined && v !== null)
    .test('positive', 'Quantity must be > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  price: yup
    .mixed<number | ''>()
    .test('required', 'Price is required', (v) => v !== '' && v !== undefined && v !== null)
    .test('positive', 'Price must be > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  p_contract_no: yup.string().trim().default(''),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<CSFixFormValues>

function fromCSFix(c: CSFixationOut): CSFixFormValues {
  return {
    contract_no: c.contract_no ?? '',
    fixation_date: c.fixation_date ?? '',
    quantity_kg: c.quantity_kg ?? '',
    price: c.price ?? '',
    p_contract_no: c.p_contract_no ?? '',
    note: c.note ?? '',
  }
}

function toApi(v: CSFixFormValues): CSFixationCreateIn {
  return {
    contract_no: v.contract_no.trim(),
    fixation_date: v.fixation_date,
    quantity_kg: typeof v.quantity_kg === 'number' ? v.quantity_kg : 0,
    price: typeof v.price === 'number' ? v.price : 0,
    p_contract_no: v.p_contract_no.trim() || null,
    note: v.note.trim() || null,
  }
}

export function CSFixationFormDialog({
  open,
  mode,
  fixedNpeContractNo,
  initialValue,
  onClose,
  onSaved,
}: CSFixationFormDialogProps) {
  const snack = useSnackbar()
  const { canEdit } = usePagePermission()
  const isEdit = mode === 'edit'

  const [createCSFix, { isLoading: creating }] = useCreateCSFixationMutation()
  const [updateCSFix, { isLoading: updating }] = useUpdateCSFixationMutation()
  const loading = creating || updating

  const { data: contractsRes } = useListAllContractsQuery({
    page: 1,
    page_size: 1_000_000,
  })
  const npeOptions = useMemo(
    () =>
      (contractsRes?.items ?? []).filter(
        (c) => c.source === 'npe' || c.contract_no?.startsWith('NPE-'),
      ),
    [contractsRes],
  )

  const { data: localPList } = useListLocalPQuery({
    page: 1,
    page_size: 1_000_000,
    is_status: 1,
  })
  const localPOptions = useMemo(() => localPList?.items ?? [], [localPList])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CSFixFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  })

  const selectedNpe = watch('contract_no')

  const npeInfo = useMemo(
    () => contractsRes?.items?.find((c) => c.contract_no === selectedNpe) ?? null,
    [contractsRes, selectedNpe],
  )

  const [supplierDisplay, setSupplierDisplay] = useState('')
  const [qualityDisplay, setQualityDisplay] = useState('')
  useEffect(() => {
    if (initialValue) {
      setSupplierDisplay(initialValue.supplier_name ?? '')
      setQualityDisplay(initialValue.quality_code ?? '')
    } else {
      setSupplierDisplay(npeInfo?.short_name ?? '')
      setQualityDisplay('')
    }
  }, [initialValue, npeInfo])

  useEffect(() => {
    if (!open) return
    if (isEdit && initialValue) {
      reset(fromCSFix(initialValue))
    } else {
      reset({ ...EMPTY, contract_no: fixedNpeContractNo ?? '' })
    }
  }, [open, isEdit, initialValue, fixedNpeContractNo, reset])

  const onSubmit = handleSubmit(
    async (values) => {
      const payload = toApi(values)
      try {
        if (isEdit && initialValue) {
          const res = await updateCSFix({
            id: initialValue.cs_fixation_id,
            body: payload,
          }).unwrap()
          snack.success('Fixation updated')
          onSaved?.(res.data?.cs_fixation_id ?? initialValue.cs_fixation_id)
        } else {
          const res = await createCSFix(payload).unwrap()
          snack.success('Fixation created')
          onSaved?.(res.data?.cs_fixation_id ?? 0)
        }
        onClose()
      } catch (err) {
        snack.error(
          getErrorMessage(err, isEdit ? 'Update failed' : 'Failed to create fixation'),
        )
      }
    },
    () => snack.error('Please fix the highlighted fields'),
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap title="Purchase - CS Fixation" sx={dialogTitleTextSx}>
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Purchase - </Box>CS Fixation
          {isEdit && initialValue?.cs_fixation_id ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              #{initialValue.cs_fixation_id}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void onSubmit()}
            disabled={loading || !canEdit}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {/* Consignment block */}
        <Typography variant="caption" color="text.secondary" sx={sectionHeaderSx}>
          Consignment
        </Typography>
        <Divider sx={{ mt: 0.5, mb: 1 }} />
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          {/* Row 1: 6 | 3 | 3 */}
          <Grid item xs={12} sm={6}>
            {fixedNpeContractNo ? (
              <TextField
                label="Consignment No."
                size="small"
                fullWidth
                value={fixedNpeContractNo}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={roSx}
              />
            ) : (
              <Controller
                control={control}
                name="contract_no"
                render={({ field }) => {
                  const found = npeOptions.find((c) => c.contract_no === field.value)
                  const selected =
                    found ??
                    (field.value
                      ? ({
                          contract_no: field.value,
                          short_name: null,
                          cp_code: null,
                          contract_date: null,
                          license_cert_no: null,
                          certification: null,
                          source: 'npe',
                        } as (typeof npeOptions)[number])
                      : null)
                  return (
                    <Autocomplete
                      size="small"
                      options={npeOptions}
                      value={selected}
                      onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                      getOptionLabel={(opt) =>
                        opt ? `${opt.contract_no} — ${opt.short_name ?? opt.cp_code ?? ''}` : ''
                      }
                      isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                      disabled={isEdit}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Consignment No. *"
                          error={!!errors.contract_no}
                          helperText={errors.contract_no?.message}
                          sx={isEdit ? roSx : undefined}
                        />
                      )}
                    />
                  )
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Fixation ID"
              size="small"
              fullWidth
              value={initialValue?.fixation_no ?? ''}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              helperText={!isEdit ? 'Auto-generated on save' : undefined}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="fixation_date"
              render={({ field }) => (
                <DatePicker
                  label="Date *"
                  format="DD-MMM-YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: !!errors.fixation_date,
                      helperText: errors.fixation_date?.message,
                    },
                  }}
                />
              )}
            />
          </Grid>

          {/* Row 2: 6 | 6 */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Consignment - Vendor"
              size="small"
              fullWidth
              value={supplierDisplay}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Consignment - Quality"
              size="small"
              fullWidth
              value={qualityDisplay}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Grid>

          {/* Row 3: 6 | 6 */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="quantity_kg"
              render={({ field }) => (
                <TextField
                  label="Quantity (Kg) *"
                  type="number"
                  size="small"
                  fullWidth
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!!errors.quantity_kg}
                  helperText={errors.quantity_kg?.message}
                  sx={numSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="price"
              render={({ field }) => (
                <TextField
                  label="Price *"
                  type="number"
                  size="small"
                  fullWidth
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!!errors.price}
                  helperText={errors.price?.message}
                  sx={numSx}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* For P Contract block */}
        <Typography variant="caption" color="text.secondary" sx={{ ...sectionHeaderSx, mt: 2, display: 'block' }}>
          For P Contract
        </Typography>
        <Divider sx={{ mt: 0.5, mb: 1 }} />
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="p_contract_no"
              render={({ field }) => {
                const selected =
                  localPOptions.find((c) => c.contract_no === field.value) ??
                  (field.value
                    ? ({
                        contract_no: field.value,
                        supplier_name: null,
                        supplier_code: null,
                      } as (typeof localPOptions)[number])
                    : null)
                return (
                  <Autocomplete
                    size="small"
                    options={localPOptions}
                    value={selected}
                    onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                    getOptionLabel={(opt) =>
                      opt
                        ? `${opt.contract_no} — ${opt.supplier_name ?? opt.supplier_code ?? ''}`
                        : ''
                    }
                    isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="For P Contract"
                        helperText={
                          errors.p_contract_no?.message ??
                          'Leave blank if BE auto-binds on allocation commit'
                        }
                      />
                    )}
                  />
                )
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              control={control}
              name="note"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Note"
                  size="small"
                  multiline
                  minRows={2}
                  fullWidth
                  error={!!errors.note}
                  helperText={errors.note?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

export default CSFixationFormDialog
